import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import paymentService from '../../services/paymentService';
import LoadingSpinner from '../UI/LoadingSpinner';
import './PaymentForm.css';

const PaymentForm = ({ 
  initialData = {}, 
  onSuccess = () => {}, 
  onError = () => {} 
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    amount: initialData.amount || '',
    customerEmail: initialData.customerEmail || '',
    customerPhone: initialData.customerPhone || '',
    customerName: initialData.customerName || '',
    description: initialData.description || '',
    ...initialData
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Get user data from localStorage or context
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    
    // Pre-fill form with user data
    if (userData.email && !formData.customerEmail) {
      setFormData(prev => ({
        ...prev,
        customerEmail: userData.email,
        customerPhone: userData.phone || '',
        customerName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
      }));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const validation = paymentService.validatePaymentData(formData);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const paymentData = {
        ...formData,
        userId: user?.id || `USER_${Date.now()}`,
        metadata: {
          source: 'web',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      };

      const result = await paymentService.initiatePayment(paymentData);
      
      if (result.success) {
        onSuccess(result);
        // The service will handle redirection to payment gateway
      } else {
        throw new Error(result.error || 'Payment initiation failed');
      }
      
    } catch (error) {
      console.error('Payment form error:', error);
      setErrors({ submit: error.message });
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="payment-form-container">
      <div className="payment-form-card">
        <div className="payment-form-header">
          <h2>Payment Details</h2>
          <p>Please fill in your payment information</p>
        </div>

        <form onSubmit={handleSubmit} className="payment-form">
          {/* Amount Field */}
          <div className="form-group">
            <label htmlFor="amount" className="form-label">
              Amount <span className="required">*</span>
            </label>
            <div className="amount-input-wrapper">
              <span className="currency-symbol">₹</span>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="0.00"
                min="1"
                step="0.01"
                className={`form-input amount-input ${errors.amount ? 'error' : ''}`}
                disabled={loading}
                required
              />
            </div>
            {errors.amount && <span className="error-message">{errors.amount}</span>}
          </div>

          {/* Customer Name Field */}
          <div className="form-group">
            <label htmlFor="customerName" className="form-label">
              Full Name
            </label>
            <input
              type="text"
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className={`form-input ${errors.customerName ? 'error' : ''}`}
              disabled={loading}
            />
            {errors.customerName && <span className="error-message">{errors.customerName}</span>}
          </div>

          {/* Email Field */}
          <div className="form-group">
            <label htmlFor="customerEmail" className="form-label">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="customerEmail"
              name="customerEmail"
              value={formData.customerEmail}
              onChange={handleInputChange}
              placeholder="your.email@example.com"
              className={`form-input ${errors.customerEmail ? 'error' : ''}`}
              disabled={loading}
              required
            />
            {errors.customerEmail && <span className="error-message">{errors.customerEmail}</span>}
          </div>

          {/* Phone Field */}
          <div className="form-group">
            <label htmlFor="customerPhone" className="form-label">
              Phone Number <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="customerPhone"
              name="customerPhone"
              value={formData.customerPhone}
              onChange={handleInputChange}
              placeholder="9876543210"
              pattern="[6-9][0-9]{9}"
              className={`form-input ${errors.customerPhone ? 'error' : ''}`}
              disabled={loading}
              required
            />
            {errors.customerPhone && <span className="error-message">{errors.customerPhone}</span>}
          </div>

          {/* Description Field */}
          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Payment Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="What is this payment for?"
              rows="3"
              className={`form-input ${errors.description ? 'error' : ''}`}
              disabled={loading}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <span>{errors.submit}</span>
            </div>
          )}

          {/* Payment Summary */}
          {formData.amount && (
            <div className="payment-summary">
              <h3>Payment Summary</h3>
              <div className="summary-row">
                <span>Amount:</span>
                <span className="amount">{paymentService.formatCurrency(formData.amount)}</span>
              </div>
              <div className="summary-row">
                <span>Payment Method:</span>
                <span>SBIePay Gateway</span>
              </div>
              <div className="summary-row total">
                <span>Total:</span>
                <span className="amount">{paymentService.formatCurrency(formData.amount)}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.amount}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="small" />
                  Processing...
                </>
              ) : (
                'Proceed to Payment'
              )}
            </button>
          </div>
        </form>

        {/* Security Notice */}
        <div className="security-notice">
          <div className="security-icon">🔒</div>
          <div className="security-text">
            <strong>Secure Payment</strong>
            <p>Your payment information is encrypted and secure. We use industry-standard security measures to protect your data.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;